import { useState, useMemo } from 'react';
import { Search, MapPin, CheckCircle2, Clock } from 'lucide-react';
import { getCountry } from './countries.js';

import { appStoreUrl, playStoreUrl } from './storeLinks.js';

const PLAY = playStoreUrl('test-centres');
const APPLE = appStoreUrl('test-centres');

function PassBadge({ rate }) {
  if (rate === null || rate === undefined) return null;
  return (
    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full shrink-0 ${
      rate < 40 ? 'bg-red-100 text-red-700' :
      rate < 48 ? 'bg-amber-100 text-amber-700' :
      rate < 60 ? 'bg-green-100 text-green-700' :
                  'bg-emerald-100 text-emerald-700'
    }`}>{rate}%</span>
  );
}

const AppleIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.62 1.54-1.53 2.97-2.53 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.02 4.49-3.74 4.25z" />
  </svg>
);

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M3.609 1.814L13.792 12 3.61 22.186a1.986 1.986 0 01-.61-.952V2.766c0-.342.22-.663.61-.952zM20.218 11.23L15.112 8.35l-2.02 2.02 2.02 2.02 5.106-2.88c.45-.25.68-.61.68-.96 0-.35-.23-.71-.68-.96zM14.414 7.555l-9.284-5.24A2.015 2.015 0 004.316 2l9.366 9.366 1.444-1.444-1.424-1.424zM13.682 12.732l-9.366 9.366a2.015 2.015 0 00.814-.316l9.284-5.24-1.444-1.444 1.424-1.424z" />
  </svg>
);

export default function CountryTestCentres({ country = 'GB', centres = [] }) {
  const C = getCountry(country);
  const cc = C.code.toLowerCase();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return centres.filter(tc => {
      const matchesQuery = q === '' || tc.name.toLowerCase().includes(q);
      const matchesFilter =
        filter === 'all' ||
        (filter === 'available' && tc.available) ||
        (filter === 'coming-soon' && !tc.available);
      return matchesQuery && matchesFilter;
    });
  }, [query, filter, centres]);

  const availableFiltered = filtered.filter(tc => tc.available);
  const comingSoonFiltered = filtered.filter(tc => !tc.available);
  const totalAvailable = centres.filter(tc => tc.available).length;
  const regions = new Set(centres.map(tc => tc.state || tc.county || tc.region || tc.province).filter(Boolean));

  return (
    <div className="pt-28 pb-24 min-h-screen bg-white">

      <section className="max-w-7xl mx-auto px-6 mb-16 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gray-100 text-gray-600 text-sm font-medium mb-6">
          <MapPin className="w-4 h-4" />
          {totalAvailable}+ {C.name} {C.regulator} {C.testCentreTerm}s
        </div>
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mb-5 leading-tight">
          {C.name} Driving Test Centre Routes
        </h1>
        <p className="text-xl text-gray-500 max-w-2xl mx-auto leading-relaxed mb-8">
          SteerClear has practice routes for <strong className="text-gray-900">{totalAvailable}+ {C.regulator} {C.testCentreTerm}s</strong> across
          {' '}{C.name}. Search your local {C.testCentreTerm}, download the app, and start
          practising the roads around your test centre.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center" data-placement="hero" data-store-pair="" data-sticky-trigger="">
          <a href={PLAY} target="_blank" rel="noopener noreferrer" data-store="play" className="inline-flex items-center justify-center gap-3 px-6 py-3.5 rounded-full bg-gray-900 text-white font-medium hover:bg-black transition-colors text-sm">
            <PlayIcon /> Download on Google Play
          </a>
          <a href={APPLE} target="_blank" rel="noopener noreferrer" data-store="ios" className="inline-flex items-center justify-center gap-3 px-6 py-3.5 rounded-full bg-gray-100 text-gray-900 font-medium hover:bg-gray-200 transition-colors text-sm">
            <AppleIcon /> Download on App Store
          </a>
        </div>
      </section>

      <section className="bg-gray-50 border-y border-gray-100 py-8 mb-16">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          <div>
            <div className="text-3xl font-bold text-gray-900">{totalAvailable}+</div>
            <div className="text-sm text-gray-500 mt-1">{C.testCentreTerm.replace(/^./, m => m.toUpperCase())}s covered</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-gray-900">{regions.size}</div>
            <div className="text-sm text-gray-500 mt-1">Regions</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-gray-900">Free</div>
            <div className="text-sm text-gray-500 mt-1">to download</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-gray-900">Genuine</div>
            <div className="text-sm text-gray-500 mt-1">{C.regulator} test routes</div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 mb-10">
        <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="search"
              placeholder={`Search ${C.testCentreTerm}…`}
              value={query}
              onChange={e => setQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent bg-white"
              aria-label={`Search ${C.testCentreTerm}s`}
            />
          </div>
          <div className="flex gap-2">
            {[
              { value: 'all', label: 'All' },
              { value: 'available', label: 'Available now' },
              { value: 'coming-soon', label: 'Coming soon' },
            ].map(opt => (
              <button
                key={opt.value}
                onClick={() => setFilter(opt.value)}
                className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  filter === opt.value ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >{opt.label}</button>
            ))}
          </div>
        </div>
        {query && (
          <p className="mt-3 text-sm text-gray-500">
            {filtered.length} result{filtered.length !== 1 ? 's' : ''} for &ldquo;{query}&rdquo;
          </p>
        )}
      </section>

      <section className="max-w-7xl mx-auto px-6">
        {(filter === 'all' || filter === 'available') && availableFiltered.length > 0 && (
          <div className="mb-12">
            {filter === 'all' && (
              <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  Available now
                  <span className="text-sm font-normal text-gray-400">({availableFiltered.length})</span>
                </h2>
                <div className="flex items-center gap-2 text-[11px] text-gray-400">
                  <span>Pass rate:</span>
                  <span className="bg-red-100 text-red-700 font-semibold px-1.5 py-0.5 rounded-full">&lt;40%</span>
                  <span className="bg-amber-100 text-amber-700 font-semibold px-1.5 py-0.5 rounded-full">40–47%</span>
                  <span className="bg-green-100 text-green-700 font-semibold px-1.5 py-0.5 rounded-full">48–59%</span>
                  <span className="bg-emerald-100 text-emerald-700 font-semibold px-1.5 py-0.5 rounded-full">60%+</span>
                </div>
              </div>
            )}
            <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {availableFiltered.map(tc => (
                <li key={tc.slug}>
                  <a
                    href={`/${cc}/test-centres/${tc.slug}`}
                    title={`${tc.name} driving test routes – practise with SteerClear`}
                    className="flex flex-col gap-2 p-3 rounded-xl border border-gray-100 hover:border-green-200 hover:bg-green-50 hover:shadow-sm bg-white transition-all group h-full"
                  >
                    <div className="flex items-start gap-2">
                      <MapPin className="w-3.5 h-3.5 text-green-600 mt-0.5 shrink-0" />
                      <span className="text-sm text-gray-800 font-medium leading-snug group-hover:text-green-900">{tc.name}</span>
                    </div>
                    {tc.passRate !== null && tc.passRate !== undefined && (
                      <div className="flex items-center gap-1 pl-5">
                        <PassBadge rate={tc.passRate} />
                        <span className="text-[10px] text-gray-400">pass rate</span>
                      </div>
                    )}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        {(filter === 'all' || filter === 'coming-soon') && comingSoonFiltered.length > 0 && (
          <div className="mb-12">
            {filter === 'all' && (
              <h2 className="text-xl font-bold text-gray-900 mb-5 flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-500" />
                Coming soon
                <span className="text-sm font-normal text-gray-400">({comingSoonFiltered.length})</span>
              </h2>
            )}
            <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {comingSoonFiltered.map(tc => (
                <li key={tc.slug}>
                  <div className="flex items-start gap-2 p-3 rounded-xl border border-dashed border-gray-200 bg-gray-50">
                    <Clock className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" />
                    <span className="text-sm text-gray-400 leading-snug">{tc.name}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {filtered.length === 0 && (
          <div className="text-center py-16 text-gray-400">
            <MapPin className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-lg font-medium">No {C.testCentreTerm}s match &ldquo;{query}&rdquo;</p>
            <p className="text-sm mt-1">Try a different spelling or city name</p>
          </div>
        )}
      </section>

      <section className="max-w-3xl mx-auto px-6 mt-16 mb-12">
        <div className="bg-gray-50 rounded-2xl p-8 border border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Practise the roads before your test</h2>
          <div className="space-y-4 text-gray-600 leading-relaxed text-sm">
            <p>
              Every {C.name} {C.practicalTest} follows a set of routes chosen by your examiner from a
              bank of approved routes specific to your local {C.testCentreTerm}. SteerClear has built
              practice routes for <strong>{totalAvailable}+ {C.testCentreTerm}s</strong> across {C.name} —
              so you can drive the roads before test day.
            </p>
            <p>
              The app uses your phone&apos;s telematics data to score every drive live —
              tracking speed control, lane discipline, roundabouts, and hazard awareness.
              You&apos;ll know exactly what to improve before you book your test.
            </p>
            <p>
              Can&apos;t see your {C.testCentreTerm} in the available list? It may be coming soon.
              Download the app and we&apos;ll notify you when routes for your centre are added.
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-3">Ready to start practising?</h2>
        <p className="text-gray-500 mb-8 text-sm">Download free — available on iOS and Android.</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center" data-placement="body" data-store-pair="">
          <a href={PLAY} target="_blank" rel="noopener noreferrer" data-store="play" className="inline-flex items-center justify-center gap-3 px-6 py-3.5 rounded-full bg-gray-900 text-white font-medium hover:bg-black transition-colors text-sm">
            <PlayIcon /> Google Play
          </a>
          <a href={APPLE} target="_blank" rel="noopener noreferrer" data-store="ios" className="inline-flex items-center justify-center gap-3 px-6 py-3.5 rounded-full bg-gray-100 text-gray-900 font-medium hover:bg-gray-200 transition-colors text-sm">
            <AppleIcon /> App Store
          </a>
        </div>
      </section>

    </div>
  );
}
